import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormHelperText,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  Close as CloseIcon,
  HelpOutline as HelpIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  WarehouseOutlined as StoresIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import axios from '../../utils/axios';
import { useNotify } from '../../hooks';
import { storeOptions } from '../../components/products/msAssortmentApi.mjs';
import FamilySlotTree from '../../components/techChain/FamilySlotTree';
import ChildrenTable from '../../components/techChain/ChildrenTable';
import {
  changedRules,
  draftFromRules,
  fmt,
  modsLabel,
  normalizeView,
  planMemberIds,
  planSavedMessage,
  sameId,
  toggleSlot,
} from '../../components/techChain/model';

const FILTERS = [
  { value: 'all', label: 'Все' },
  { value: 'unset', label: 'Не настроены' },
  { value: 'attention', label: 'Требуют внимания' },
];

function matchesFilter(family, filter) {
  if (filter === 'unset') return !family.rule_set;
  if (filter === 'attention') return Number(family.issues_count) > 0;
  return true;
}

function namesOf(issues, kind) {
  return issues.filter((issue) => issue.kind === kind).map((issue) => issue.name).filter(Boolean);
}

function FamilyIssues({ issues }) {
  const list = Array.isArray(issues) ? issues : [];
  const ambiguous = namesOf(list, 'ambiguous');
  const unresolved = namesOf(list, 'unresolved');
  const ambiguousCount = list.filter((issue) => issue.kind === 'ambiguous').length;
  const unresolvedCount = list.filter((issue) => issue.kind === 'unresolved').length;
  const noRule = list.some((issue) => issue.kind === 'no_rule');
  if (!noRule && !ambiguousCount && !unresolvedCount) return null;
  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      {noRule && (
        <Alert severity="warning">
          Добавка не считается: отметьте, какие материалы учитывать, и сохраните
        </Alert>
      )}
      {ambiguousCount > 0 && (
        <Alert severity="warning">
          {ambiguous.length
            ? `Нужно выбрать техкарту: ${ambiguous.join(', ')}`
            : 'Нужно выбрать техкарту — без выбора материал не учитывается'}
        </Alert>
      )}
      {unresolvedCount > 0 && (
        <Alert severity="error">
          {unresolved.length
            ? `Параметрика техкарты не разрешилась: ${unresolved.join(', ')}`
            : 'Параметрика техкарты не разрешилась'}
        </Alert>
      )}
    </Stack>
  );
}

function HelpDialog({ open, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Как считается остаток на МП</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2">
            На маркетплейс уходит свой остаток товара плюс добавка — сколько ещё можно собрать по техкартам.
            Если материалов несколько, итог ограничивает узкое место.
          </Typography>
          <Typography variant="body2">
            Какие слоты входят в добавку, задаётся на родителе: чехол, крой, ткань и другие материалы семьи.
            Правило одно на всю семью материала, цвета и размеры подходят сами. Снятый слот в расчёт не входит.
          </Typography>
          <Typography variant="body2">
            Пока правило не сохранено, добавка по этому узлу равна нулю — на МП уходит только свой остаток.
            Внутрь отмеченного полуфабриката считаем только по его правилу: пока чехол не настроен, ткань к нему не прибавляется.
          </Typography>
          <Typography variant="body2">
            Галочки можно менять до сохранения и сразу смотреть пересчёт. Параметрическая техкарта родителя
            записывается на всю семью, если она у него одна; размерные параметрики и обычные карты — только на
            эту модификацию (подпись под выбором показывает, как сохранилось). При отправке на МП принуд кабинета
            перекрывает расчёт.
            Склады полуфабрикатов и ткани общие, кнопка «Склады».
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Понятно</Button>
      </DialogActions>
    </Dialog>
  );
}

function StoresDialog({ open, onClose, onSaved }) {
  const { error: showError, success: showSuccess } = useNotify();
  const [stores, setStores] = useState([]);
  const [productIds, setProductIds] = useState([]);
  const [semiIds, setSemiIds] = useState([]);
  const [fabricIds, setFabricIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      axios.get('/tech-chain/settings'),
      axios.get('/mswb/ms-stores', { silent: true }).catch(() => ({ data: [] })),
    ])
      .then(([{ data }, storesRes]) => {
        setStores(storeOptions(storesRes.data));
        setProductIds(data?.product_store_ids || []);
        setSemiIds(data?.settings?.semi_store_ids || []);
        setFabricIds(data?.settings?.fabric_store_ids || []);
      })
      .catch((e) => showError(e?.response?.data?.message || 'Не удалось загрузить склады'))
      .finally(() => setLoading(false));
  }, [open, showError]);

  const nameOf = (id) => stores.find((store) => store.id === id)?.name || id;
  const picker = (label, value, onChange) => (
    <Autocomplete
      multiple
      options={stores}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      value={value.map((id) => stores.find((store) => store.id === id) || { id, name: id })}
      onChange={(_, val) => onChange(val.map((item) => item.id))}
      renderInput={(params) => (
        <TextField {...params} label={label} helperText="Пусто — те же, что для товара" />
      )}
    />
  );

  const save = async () => {
    setSaving(true);
    try {
      await axios.put('/tech-chain/settings', { semi_store_ids: semiIds, fabric_store_ids: fabricIds });
      showSuccess('Склады сохранены');
      onClose();
      onSaved();
    } catch (e) {
      showError(e?.response?.data?.message || 'Не удалось сохранить склады');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Склады МС для расчёта</DialogTitle>
      <DialogContent>
        {loading ? <LinearProgress /> : (
          <Stack spacing={3} sx={{ pt: 1 }}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>Склады товара</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {productIds.length
                  ? productIds.map((id) => <Chip key={id} size="small" label={nameOf(id)} />)
                  : <Typography variant="body2" color="text.secondary">Не выбраны ни в одной связке</Typography>}
              </Stack>
              <FormHelperText>
                Задаются в каждой связке («Товары МС» → «Склады» → «Связь с МП»); на этой странице — все вместе.
              </FormHelperText>
            </Box>
            {picker('Склады чехлов и полуфабрикатов', semiIds, setSemiIds)}
            {picker('Склады ткани', fabricIds, setFabricIds)}
            <Alert severity="info">Эти склады общие для всех кабинетов и связок, в том числе при отправке остатков на МП.</Alert>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button variant="contained" onClick={save} disabled={loading || saving}>Сохранить</Button>
      </DialogActions>
    </Dialog>
  );
}

function FamilyPanel({
  family,
  display,
  savedRules,
  draftRules,
  dirty,
  loading,
  previewing,
  error,
  savingRules,
  savingPlanId,
  onToggle,
  onPlanChange,
  onSave,
  onCancel,
}) {
  const name = display?.parent?.name || family?.name || 'Семья';
  if (!family && !display && !loading) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="body2" color="text.secondary" align="center">
            Выберите семью в списке
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" noWrap title={name}>{name}</Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                <Chip
                  size="small"
                  color={family?.rule_set ? 'success' : 'warning'}
                  label={family?.rule_set ? 'настроено' : 'не настроено'}
                />
                {dirty && <Chip size="small" color="warning" label="не сохранено" />}
              </Stack>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button onClick={onCancel} disabled={!dirty || savingRules}>Отменить</Button>
              <Button variant="contained" onClick={onSave} disabled={!dirty || savingRules || loading}>
                {savingRules ? 'Сохранение…' : 'Сохранить'}
              </Button>
            </Stack>
          </Stack>
          {previewing && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary">пересчёт…</Typography>
              <LinearProgress />
            </Box>
          )}
          {loading && !previewing && <LinearProgress sx={{ mt: 1.5 }} />}
        </CardContent>
        {!loading && error && !display && (
          <>
            <Divider />
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Не удалось загрузить семью
              </Typography>
            </CardContent>
          </>
        )}
        {display && (
          <>
            <Divider />
            <CardContent>
              <FamilyIssues issues={display.issues} />
              <FamilySlotTree
                tree={display.tree}
                familyChildren={display.children}
                savedRules={savedRules}
                draftRules={draftRules}
                savingPlanId={savingPlanId}
                onToggle={onToggle}
                onPlanChange={onPlanChange}
              />
            </CardContent>
          </>
        )}
      </Card>

      {display && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>Модификации</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              Цифры для текущего черновика — то, что уйдёт на МП после сохранения (принуд кабинета перекрывает)
            </Typography>
            <ChildrenTable
              key={display.parent?.id ?? 'family'}
              rows={display.children}
              savingPlanId={savingPlanId}
              onPlanChange={onPlanChange}
            />
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}

export default function TechChainCheckMui() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  const { success: showSuccess, error: showError } = useNotify();
  const [families, setFamilies] = useState([]);
  const [familiesLoading, setFamiliesLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [savedView, setSavedView] = useState(null);
  const [display, setDisplay] = useState(null);
  const [draftRules, setDraftRules] = useState({});
  const [familyLoading, setFamilyLoading] = useState(false);
  const [familyError, setFamilyError] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [savingRules, setSavingRules] = useState(false);
  const [savingPlanId, setSavingPlanId] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [storesOpen, setStoresOpen] = useState(false);

  const draftRef = useRef(draftRules);
  const savedViewRef = useRef(savedView);
  const loadedForRef = useRef(null);
  const previewTimer = useRef(null);
  const previewSeq = useRef(0);
  const familySeq = useRef(0);
  draftRef.current = draftRules;
  savedViewRef.current = savedView;

  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;

  const schedulePreview = useCallback((parentId, rules, delay = 300) => {
    if (parentId == null) return;
    clearTimeout(previewTimer.current);
    const seq = ++previewSeq.current;
    setPreviewing(true);
    previewTimer.current = setTimeout(async () => {
      try {
        const { data } = await axios.post('/tech-chain/preview', {
          parent_id: parentId,
          rules,
        }, { silent: true });
        if (seq !== previewSeq.current) return;
        const next = normalizeView(data);
        next.rules = savedViewRef.current?.rules || {};
        setDisplay(next);
      } catch (e) {
        if (seq !== previewSeq.current) return;
        showErrorRef.current(e?.response?.data?.message || 'Не удалось пересчитать');
      } finally {
        if (seq === previewSeq.current) setPreviewing(false);
      }
    }, delay);
  }, []);

  const loadFamilies = useCallback(async () => {
    setFamiliesLoading(true);
    try {
      const { data } = await axios.get('/tech-chain/families', { silent: true });
      setFamilies(Array.isArray(data?.families) ? data.families : []);
    } catch (e) {
      setFamilies([]);
      showErrorRef.current(e?.response?.data?.message || 'Не удалось загрузить семьи');
    } finally {
      setFamiliesLoading(false);
    }
  }, []);

  const loadFamily = useCallback(async (parentId, { preserveDirty = false, quiet = false } = {}) => {
    clearTimeout(previewTimer.current);
    previewSeq.current += 1;
    setPreviewing(false);
    const seq = ++familySeq.current;
    const switching = !sameId(loadedForRef.current, parentId);
    if (switching) {
      setDisplay(null);
      setDraftRules({});
      draftRef.current = {};
      setFamilyLoading(true);
    } else if (!quiet) {
      setFamilyLoading(true);
    }
    setFamilyError(false);
    try {
      const { data } = await axios.get(`/tech-chain/family/${encodeURIComponent(parentId)}`, { silent: true });
      if (seq !== familySeq.current) return;
      const dirty = preserveDirty ? changedRules(savedViewRef.current?.rules, draftRef.current) : null;
      const view = normalizeView(data);
      savedViewRef.current = view;
      loadedForRef.current = parentId;
      setSavedView(view);
      if (dirty && Object.keys(dirty).length) {
        const nextDraft = { ...draftFromRules(view.rules), ...dirty };
        draftRef.current = nextDraft;
        setDraftRules(nextDraft);
        setDisplay(view);
        schedulePreview(parentId, nextDraft, 0);
      } else {
        const nextDraft = draftFromRules(view.rules);
        draftRef.current = nextDraft;
        setDraftRules(nextDraft);
        setDisplay(view);
      }
    } catch (e) {
      if (seq !== familySeq.current) return;
      if (switching) {
        setSavedView(null);
        savedViewRef.current = null;
        loadedForRef.current = null;
        setDisplay(null);
        setDraftRules({});
        draftRef.current = {};
      }
      setFamilyError(true);
      showErrorRef.current(e?.response?.data?.message || 'Не удалось загрузить семью');
    } finally {
      if (seq === familySeq.current) setFamilyLoading(false);
    }
  }, [schedulePreview]);

  useEffect(() => { loadFamilies(); }, [loadFamilies]);

  useEffect(() => {
    if (selectedId == null) {
      familySeq.current += 1;
      previewSeq.current += 1;
      clearTimeout(previewTimer.current);
      setPreviewing(false);
      setFamilyLoading(false);
      setFamilyError(false);
      setSavedView(null);
      savedViewRef.current = null;
      loadedForRef.current = null;
      setDisplay(null);
      setDraftRules({});
      draftRef.current = {};
      return undefined;
    }
    loadFamily(selectedId);
    return undefined;
  }, [selectedId, loadFamily]);

  useEffect(() => () => {
    clearTimeout(previewTimer.current);
    previewSeq.current += 1;
    familySeq.current += 1;
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return families.filter((family) => {
      if (!matchesFilter(family, filter)) return false;
      if (!q) return true;
      return String(family.name || '').toLowerCase().includes(q);
    });
  }, [families, query, filter]);

  useEffect(() => {
    const stillVisible = filtered.some((family) => sameId(family.parent_id, selectedId));
    if (isMobile) {
      if (selectedId != null && !stillVisible) setSelectedId(null);
      return;
    }
    if (!filtered.length) {
      if (selectedId != null) setSelectedId(null);
      return;
    }
    if (!stillVisible) setSelectedId(filtered[0].parent_id);
  }, [filtered, isMobile, selectedId]);

  const selectedFamily = families.find((family) => sameId(family.parent_id, selectedId)) || null;
  const dirty = Object.keys(changedRules(savedView?.rules, draftRules)).length > 0;

  const handleToggle = (node) => {
    if (node?.owner_id == null || node.key == null || selectedId == null) return;
    const next = toggleSlot(draftRef.current, savedViewRef.current?.rules, node.owner_id, node.key);
    draftRef.current = next;
    setDraftRules(next);
    schedulePreview(selectedId, next);
  };

  const handlePlanChange = async (msId, planId, node) => {
    if (selectedId == null) return;
    setSavingPlanId(msId);
    try {
      const { data } = await axios.put('/tech-chain/plan', {
        ms_id: msId,
        processing_plan_id: planId,
        member_ids: planMemberIds(node),
      }, { silent: true });
      showSuccess(planSavedMessage(planId, data?.scope));
      await loadFamilies();
      await loadFamily(selectedId, { preserveDirty: true, quiet: true });
    } catch (e) {
      showError(e?.response?.data?.message || 'Не удалось сохранить техкарту');
    } finally {
      setSavingPlanId(null);
    }
  };

  const handleSave = async () => {
    if (selectedId == null) return;
    const rules = changedRules(savedViewRef.current?.rules, draftRef.current);
    if (!Object.keys(rules).length) return;
    setSavingRules(true);
    try {
      await axios.put('/tech-chain/rules', { rules }, { silent: true });
      showSuccess('Правила сохранены');
      await loadFamilies();
      await loadFamily(selectedId, { quiet: true });
    } catch (e) {
      showError(e?.response?.data?.message || 'Не удалось сохранить правила');
    } finally {
      setSavingRules(false);
    }
  };

  const handleCancel = () => {
    clearTimeout(previewTimer.current);
    previewSeq.current += 1;
    setPreviewing(false);
    const next = draftFromRules(savedViewRef.current?.rules);
    draftRef.current = next;
    setDraftRules(next);
    if (savedViewRef.current) setDisplay(savedViewRef.current);
  };

  const refresh = () => {
    loadFamilies();
    if (selectedId != null) loadFamily(selectedId);
  };

  const panel = (
    <FamilyPanel
      family={selectedFamily}
      display={display}
      savedRules={savedView?.rules}
      draftRules={draftRules}
      dirty={dirty}
      loading={familyLoading}
      previewing={previewing}
      error={familyError}
      savingRules={savingRules}
      savingPlanId={savingPlanId}
      onToggle={handleToggle}
      onPlanChange={handlePlanChange}
      onSave={handleSave}
      onCancel={handleCancel}
    />
  );

  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent sx={{ pb: 1 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Поиск по семье"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              autoComplete="off"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Button startIcon={<StoresIcon />} onClick={() => setStoresOpen(true)}>
                Склады
              </Button>
              <Button startIcon={<HelpIcon />} onClick={() => setHelpOpen(true)}>
                Как считается
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={refresh}
                disabled={familiesLoading}
              >
                Обновить
              </Button>
            </Stack>
          </Stack>
        </CardContent>
        <Tabs
          value={filter}
          onChange={(_, value) => setFilter(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 1 }}
        >
          {FILTERS.map((item) => (
            <Tab key={item.value} value={item.value} label={item.label} />
          ))}
        </Tabs>
      </Card>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card variant="outlined">
            {familiesLoading && <LinearProgress />}
            {!familiesLoading && filtered.length === 0 ? (
              <CardContent>
                <Typography variant="body2" color="text.secondary" align="center">
                  {families.length === 0 && !query && filter === 'all'
                    ? 'Нет семей со связью на маркетплейсах'
                    : 'Ничего не найдено'}
                </Typography>
              </CardContent>
            ) : (
              <List dense disablePadding sx={{ maxHeight: { md: '70vh' }, overflow: 'auto' }}>
                {filtered.map((family) => (
                  <ListItemButton
                    key={family.parent_id}
                    divider
                    selected={sameId(family.parent_id, selectedId)}
                    onClick={() => setSelectedId(family.parent_id)}
                  >
                    <ListItemText
                      primary={family.name}
                      secondary={`${modsLabel(family.children_count)} · итого ${fmt(family.total)}`}
                      slotProps={{
                        primary: { noWrap: true, title: family.name },
                        secondary: { noWrap: true },
                      }}
                    />
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0, ml: 1 }}>
                      {Number(family.issues_count) > 0 && (
                        <Tooltip title={`Требует внимания: ${family.issues_count}`}>
                          <WarningIcon fontSize="small" color="warning" />
                        </Tooltip>
                      )}
                      <Chip
                        size="small"
                        variant="outlined"
                        color={family.rule_set ? 'success' : 'warning'}
                        label={family.rule_set ? 'настроено' : 'не настроено'}
                      />
                    </Stack>
                  </ListItemButton>
                ))}
              </List>
            )}
          </Card>
        </Grid>

        {!isMobile && (
          <Grid size={{ xs: 12, md: 8 }}>
            {panel}
          </Grid>
        )}
      </Grid>

      {isMobile && (
        <Dialog fullScreen open={selectedId != null} onClose={() => setSelectedId(null)}>
          <DialogTitle sx={{ pr: 7 }}>
            {display?.parent?.name || selectedFamily?.name || 'Семья'}
            <IconButton
              aria-label="Закрыть"
              onClick={() => setSelectedId(null)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>{panel}</DialogContent>
        </Dialog>
      )}

      <HelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
      <StoresDialog
        open={storesOpen}
        onClose={() => setStoresOpen(false)}
        onSaved={() => {
          loadFamilies();
          if (selectedId != null) loadFamily(selectedId, { preserveDirty: true, quiet: true });
        }}
      />
    </Stack>
  );
}
